import { useCallback } from 'react';
import { useAtom } from 'jotai';
import {
    paymentPopupOpenAtom,
    paymentPopupUrlSegmentAtom,
    paymentPopupStripeAccountAtom,
    paymentPopupPaymentIdAtom,
    paymentPopupPublishableKeyAtom,
    paymentPopupDisplayedAmountAtom,
} from '../states/payment-popup-atoms';

/**
 * Custom hook for managing payment popup state and logic
 * @returns Object containing popup state and control functions
 */
export const usePaymentPopup = () => {
    const [isPaymentPopupOpen, setIsPaymentPopupOpen] =
        useAtom(paymentPopupOpenAtom);
    const [currentUrlSegment, setCurrentUrlSegment] = useAtom(
        paymentPopupUrlSegmentAtom,
    );
    const [stripeAccount, setStripeAccount] = useAtom(
        paymentPopupStripeAccountAtom,
    );
    const [currentPaymentId, setCurrentPaymentId] = useAtom(
        paymentPopupPaymentIdAtom,
    );
    const [publishableKey, setPublishableKey] = useAtom(
        paymentPopupPublishableKeyAtom,
    );
    const [displayedAmount, setDisplayedAmount] = useAtom(
        paymentPopupDisplayedAmountAtom,
    );

    const openPopup = useCallback(
        (
            urlSegment: string,
            stripeAccountParam: string | null,
            paymentId?: number,
            publishableKeyParam?: string | null,
            displayedAmountParam?: number | null,
        ) => {
            setCurrentUrlSegment(urlSegment);
            setIsPaymentPopupOpen(true);
            setStripeAccount(stripeAccountParam);
            setCurrentPaymentId(paymentId || 0);
            setPublishableKey(publishableKeyParam || null);
            setDisplayedAmount(displayedAmountParam ?? null);
        },
        [
            setCurrentUrlSegment,
            setIsPaymentPopupOpen,
            setStripeAccount,
            setCurrentPaymentId,
            setPublishableKey,
            setDisplayedAmount,
        ],
    );

    const closePopup = useCallback(() => {
        setIsPaymentPopupOpen(false);
        setCurrentUrlSegment('');
        setStripeAccount(null);
        setCurrentPaymentId(0);
        setPublishableKey(null);
        setDisplayedAmount(null);
    }, [
        setIsPaymentPopupOpen,
        setCurrentUrlSegment,
        setStripeAccount,
        setCurrentPaymentId,
        setPublishableKey,
        setDisplayedAmount,
    ]);

    return {
        isPaymentPopupOpen,
        openPopup,
        closePopup,
        currentUrlSegment,
        stripeAccount,
        currentPaymentId,
        publishableKey,
        displayedAmount,
    };
};
